import type { DesignRequest, Schedule, Screen } from '@ghostscreen/contracts';
import { i18n } from '../../i18n';
import { getCardConnectionStatus } from '../../lib/dashboard/screenStatus';
import type { HomePlaylistCard } from './buildHomePlaylistCards';

export type HomeQuickActionId = 'create-playlist' | 'request-design' | 'assign-screens' | 'schedule';

export type HomeQuickAction = {
  id: HomeQuickActionId;
  title: string;
  /** Live, organisation-specific line under the title. */
  hint: string;
  to: string;
  /** True when the hint points at something waiting on the user. */
  highlighted: boolean;
};

function createPlaylistAction(cards: HomePlaylistCard[]): HomeQuickAction {
  const empty = cards.filter((card) => card.itemCount === 0).length;
  return {
    id: 'create-playlist',
    title: i18n.t('home:quickActions.createPlaylist.title'),
    hint:
      cards.length === 0
        ? i18n.t('home:quickActions.createPlaylist.hintFirst')
        : empty > 0
          ? i18n.t('home:quickActions.createPlaylist.hintEmpty', { count: empty })
          : i18n.t('home:quickActions.createPlaylist.hintLibrary', { count: cards.length }),
    to: '/playlists#new',
    highlighted: false,
  };
}

function requestDesignAction(designRequests: DesignRequest[] | null): HomeQuickAction {
  const inReview = designRequests?.filter((request) => request.status === 'review').length ?? 0;
  const inProgress =
    designRequests?.filter((request) => request.status === 'sent' || request.status === 'designing')
      .length ?? 0;
  return {
    id: 'request-design',
    title: i18n.t('home:quickActions.requestDesign.title'),
    hint:
      inReview > 0
        ? i18n.t('home:quickActions.requestDesign.hintReview', { count: inReview })
        : inProgress > 0
          ? i18n.t('home:quickActions.requestDesign.hintInProgress', { count: inProgress })
          : i18n.t('home:quickActions.requestDesign.hintDefault'),
    to: '/designer',
    highlighted: inReview > 0,
  };
}

function assignScreensAction(
  cards: HomePlaylistCard[],
  screens: Screen[],
): HomeQuickAction {
  const paired = screens.filter((screen) => getCardConnectionStatus(screen) !== 'unpaired').length;
  const unassigned = cards.filter((card) => card.itemCount > 0 && card.screenCount === 0).length;
  return {
    id: 'assign-screens',
    title: i18n.t('home:quickActions.assignScreens.title'),
    hint:
      paired === 0
        ? i18n.t('home:quickActions.assignScreens.hintPair')
        : unassigned > 0
          ? i18n.t('home:quickActions.assignScreens.hintUnassigned', { count: unassigned })
          : i18n.t('home:quickActions.assignScreens.hintReady', { count: paired }),
    to: paired === 0 ? '/screens#setup' : '/screens',
    highlighted: paired > 0 && unassigned > 0,
  };
}

function scheduleAction(schedules: Schedule[] | null): HomeQuickAction {
  const count = schedules?.length ?? 0;
  return {
    id: 'schedule',
    title: i18n.t('home:quickActions.schedule.title'),
    hint:
      schedules === null || count === 0
        ? i18n.t('home:quickActions.schedule.hintDefault')
        : i18n.t('home:quickActions.schedule.hintCount', { count }),
    to: '/schedule',
    highlighted: false,
  };
}

/**
 * Home shortcuts with hints from the organisation's own data. Every action is
 * a write, so people who can't manage content get none.
 * `designRequests` / `schedules` are null when they couldn't be loaded.
 */
export function buildHomeQuickActions(input: {
  cards: HomePlaylistCard[];
  screens: Screen[];
  designRequests: DesignRequest[] | null;
  schedules: Schedule[] | null;
  canManage: boolean;
}): HomeQuickAction[] {
  if (!input.canManage) return [];
  return [
    createPlaylistAction(input.cards),
    requestDesignAction(input.designRequests),
    assignScreensAction(input.cards, input.screens),
    scheduleAction(input.schedules),
  ];
}
